import { Button, DialogContent, DialogTitle, TextField } from "@mui/material";
import { useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ObjectMetricExtension } from "@entrilia/shared/api/dataCollectionTypes";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { combineValidators, requiredValidator, uniqueValidator } from "@entrilia/shared/utilities/validators";
import adminApi from "../../../../api/adminApi";
import type { ExtendedMetricInfo } from "./metricExtensionsManagementState";

const createObjectMetricExtensions = withErrorHandling(adminApi.createObjectMetricExtensions);

interface Props {
  recipientObjectId: string;
  extendedMetric: ExtendedMetricInfo;
  existingExtensionValues: string[];
  onClose: () => void;
  onCreated: (newExtensions: ObjectMetricExtension[]) => void;
}

const AddNewMetricExtensionDialog = ({
  onClose,
  recipientObjectId,
  extendedMetric,
  existingExtensionValues,
  onCreated,
}: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [value, setValue] = useState("");
  const [isTouched, setIsTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  const validateValue = useMemo(
    () =>
      combineValidators(
        requiredValidator,
        uniqueValidator("An extension with this name already exists", existingExtensionValues)
      ),
    [existingExtensionValues]
  );

  const handleAdd = async () => {
    setSaving(true);

    const [newExtensions, error] = await createObjectMetricExtensions({
      objectId: recipientObjectId,
      metricExtensions: extendedMetric.metricIds.map((metricId) => ({ metricId, value: value.trim() })),
    });

    setSaving(false);

    if (error) {
      logError(error, "[AddNewMetricExtensionDialog] createObjectMetricExtensions");
      sendNotificationError(error.message);
      return;
    }

    onCreated(newExtensions);
    sendNotification("Extension added successfully");
  };

  const handleValueChange = (value: string) => {
    setValue(value);
    setIsTouched(true);
  };

  const validationResult = validateValue(value);

  return (
    <SmallDialog open>
      <DialogTitle>Add Extension for {extendedMetric.name}</DialogTitle>
      <DialogCloseButton onClick={onClose} />

      <DialogContent>
        <TextField
          fullWidth
          value={value}
          onChange={(e) => handleValueChange(e.target.value)}
          error={!validationResult.isValid && isTouched}
          helperText={isTouched ? validationResult.error : ""}
        />
      </DialogContent>

      <DialogButtons onCancel={onClose}>
        <Button
          color="primary"
          variant="contained"
          onClick={handleAdd}
          disabled={!validationResult.isValid}
          loading={saving}
        >
          Add
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default AddNewMetricExtensionDialog;
